import { RelatedToType } from '@shared/enums';
import { Observable, switchMap } from 'rxjs';
import { GraphqlParams, GraphqlResultInfo } from '@shared/models/graphql';
import { Project, ProjectTemplate, Property } from 'app/modules/projects/projects.types';
import { Entity } from '../types';
import { JupiterDataService } from './jupiter-data.service';
import { SavedView } from '@core/models';
import { ExportOptions } from '@shared/models/export-options';
import { Image } from 'app/modules/projects/projects.types';
import { CustomObject } from '@shared/modules/custom-objects/custom-objects.types';
import { Contact } from 'app/modules/contacts/contacts.types';
import { Task } from '@tasks/tasks.types';
import { LoanQuote } from 'app/modules/projects/details/loans/loans.types';
import { RelatedToFilter } from './service.types';
import { PropertyUnit } from '@modules/projects/details/leases/tenants.types';

type CustomObjectPayload = { data: any; template_id?: number };

export class JupiterService {
  private get relatedTo(): RelatedToType | string {
    return this.tsObject.name.includes('co_') ? RelatedToType.CustomObject : this.tsObject.name;
  }

  constructor(
    protected readonly dataService: JupiterDataService,
    protected readonly tsObject: CustomObject,
  ) {}

  createTemplate(template: ProjectTemplate<any>): Observable<any> {
    return this.dataService.layoutsService.create(template);
  }

  updateTemplate(template: ProjectTemplate<any>): Observable<any> {
    return this.dataService.layoutsService.update(template);
  }

  addView(view: SavedView): Observable<SavedView> {
    return this.dataService.savedViewService.create(view);
  }

  getView(id: number): Observable<SavedView> {
    return this.dataService.savedViewService.getById(id);
  }

  deleteView(id: number): Observable<SavedView> {
    return this.dataService.savedViewService.delete(id);
  }

  updateView(view: SavedView, onlyView = false): Observable<SavedView> {
    if (onlyView) {
      return this.dataService.savedViewService.update(view);
    }

    return this.dataService.savedFilterService
      .update(view.saved_filter)
      .pipe(switchMap(() => this.dataService.savedViewService.update(view)));
  }

  getViews(): Observable<any> {
    return this.dataService.savedViewService.get(this.tsObject.uiConfig?.savedViewType ?? this.tsObject.name);
  }

  search<T>(params: GraphqlParams, exportOptions?: ExportOptions): Observable<GraphqlResultInfo<T>> {
    if (!params.fields.find((x) => x === 'id')) {
      params.fields = [...params.fields, 'id'];
    }

    return this.dataService.graphqlService.query(params, exportOptions);
  }

  loadEntity(id: number, select?: string[]): Observable<Entity> {
    if (this.relatedTo === RelatedToType.CustomObject) {
      return this.dataService.customObjectService.getValueById<Entity>(
        id,
        this.tsObject.id,
        this.dataService.accountId,
      );
    }

    return this.apiCall(this.dataService.apiService.getById, id, select);
  }

  loadEntityByType(id: number, type: CustomObject, select?: string[]): Observable<Entity> {
    if (type.id) {
      return this.dataService.customObjectService.getValueById<Entity>(id, type.id, this.dataService.accountId);
    }

    return this.dataService.apiService.getById.bind({ tsObject: type, httpClient: this.dataService.httpClient })(
      id,
      select,
    );
  }

  updateEntity(entity: any): Observable<any> {
    if (this.relatedTo === RelatedToType.CustomObject) {
      const payload = this.buildCustomObjectPayload(entity);

      return this.dataService.customObjectService.updateValue(
        this.dataService.accountId,
        this.tsObject.id,
        entity.id,
        payload,
      );
    }

    return this.apiCall(this.dataService.apiService.update, entity);
  }

  updateEntityByType(entity: any, type: CustomObject): Observable<any> {
    if (type.id) {
      const payload = this.buildCustomObjectPayload(entity);

      return this.dataService.customObjectService.updateValue.bind({
        tsObject: type,
        httpClient: this.dataService.httpClient,
      })(this.dataService.accountId, type.id, entity.id, payload);
    }

    return this.apiCall(
      this.dataService.apiService.update.bind({ tsObject: type, httpClient: this.dataService.httpClient }),
      entity,
    );
  }

  updateEntityTemplate(entityId: number, templateId: number): Observable<any> {
    if (this.relatedTo === RelatedToType.CustomObject) {
      return this.dataService.customObjectService.updateValue(this.dataService.accountId, this.tsObject.id, entityId, {
        template_id: templateId,
      });
    }

    return this.apiCall(this.dataService.apiService.update, {
      id: entityId,
      template_id: templateId,
    });
  }

  addEntity(entity: any): Observable<any> {
    if (this.relatedTo === RelatedToType.CustomObject) {
      const payload = this.normalizeTemplateIdPayload({ data: entity });

      return this.dataService.customObjectService.createValue(this.dataService.accountId, this.tsObject.id, payload);
    }

    return this.apiCall(this.dataService.apiService.add, entity);
  }

  deleteEntity(id: number, forceDelete = false): Observable<any> {
    if (this.relatedTo === RelatedToType.CustomObject) {
      return this.dataService.customObjectService.deleteValue(
        this.dataService.accountId,
        this.tsObject.id,
        id,
        forceDelete,
      );
    }

    return this.apiCall(this.dataService.apiService.delete, id, forceDelete);
  }

  duplicateEntity(id: number): Observable<any> {
    if (this.relatedTo === RelatedToType.CustomObject) {
      return this.dataService.customObjectService.duplicateValue(this.dataService.accountId, this.tsObject.id, id);
    }

    throw new Error('Not supported operation');
  }

  createContactAsVCard(file: File): Observable<any> {
    if (this.relatedTo === RelatedToType.Contact) {
      return this.apiCall(this.dataService.contactsService.createContactAsVCard, file);
    }

    throw new Error('Not supported operation');
  }

  updateAvatar(entityId: number, file: File): Observable<any> {
    if (![RelatedToType.Company, RelatedToType.Contact].includes(this.tsObject.name as RelatedToType)) {
      throw new Error('Not supported operation');
    }

    return this.apiCall(this.dataService.apiService.updateAvatar, entityId, file);
  }

  deleteAvatar(entityId: number): Observable<any> {
    if (![RelatedToType.Company, RelatedToType.Contact].includes(this.tsObject.name as RelatedToType)) {
      throw new Error('Not supported operation');
    }

    return this.apiCall(this.dataService.apiService.deleteAvatar, entityId);
  }

  uploadImage(relatedToId: number, file: File): Observable<Image> {
    let paramName = 'value_id';
    if (this.tsObject.name === RelatedToType.Project) {
      paramName = 'project_id';
    } else if (this.tsObject.name === RelatedToType.Property) {
      paramName = 'property_id';
    }

    return this.dataService.imageService.upload(relatedToId, file, paramName);
  }

  deleteImage(id: number): Observable<Image> {
    return this.dataService.imageService.delete(id);
  }

  getEngagements(entityId: number): Observable<any> {
    return this.dataService.engagementService.getEngagements(entityId, this.tsObject.name as RelatedToType);
  }

  getRelatedContacts(filter: RelatedToFilter): Observable<Contact[]> {
    return this.apiCall(this.dataService.apiService.getRelatedContacts, filter);
  }

  createContactAssociation(model: {
    contact: Contact;
    entityId: number;
    relatedToType: RelatedToType;
  }): Observable<Contact> {
    return this.apiCall(this.dataService.apiService.createContactAssociation, model);
  }

  deleteContactAssociation(model: {
    associationId: number;
    entityId: number;
    relatedToType: RelatedToType;
  }): Observable<any> {
    return this.apiCall(this.dataService.apiService.deleteContactAssociation, model);
  }

  getRelatedTasks(entityId: number): Observable<Task[]> {
    return this.apiCall(this.dataService.apiService.getRelatedTasks, entityId);
  }

  getRelatedLoanQuotes(filter: RelatedToFilter): Observable<LoanQuote[]> {
    return this.apiCall(this.dataService.apiService.getRelatedLoanQuotes, filter);
  }

  getRelatedDeals(filter: RelatedToFilter): Observable<Project[]> {
    return this.apiCall(this.dataService.apiService.getRelatedDeals, filter);
  }

  getRelatedProperties(filter: RelatedToFilter): Observable<Property[]> {
    return this.apiCall(this.dataService.apiService.getRelatedProperties, filter);
  }

  deleteEngagement(id: number): Observable<any> {
    return this.dataService.engagementService.deleteEngagement(id);
  }

  getRelatedPropertyUnits(filter: RelatedToFilter): Observable<PropertyUnit[]> {
    return this.dataService.propertyUnitService.search(filter.entityId);
  }
  updatePropertyUnit(entityId: number, propertyUnit: PropertyUnit): Observable<PropertyUnit> {
    return this.dataService.propertyUnitService.update(entityId, propertyUnit);
  }

  generateMergeDocument(data: {
    entityId: number;
    accountId: number;
    fileResourceId: number;
    format: string;
    sendToDocusign: boolean;
  }): Observable<any> {
    if (this.tsObject.id) {
      return this.dataService.customObjectService.generateCustomObjectValueDocument(
        data.accountId,
        this.tsObject.id,
        data.entityId,
        data.format,
        data.fileResourceId,
        data.sendToDocusign,
      );
    } else {
      throw new Error('Not supported operation');
    }
  }

  private apiCall<T>(fn: (...args: any[]) => T, ...args: any[]): T {
    return fn.bind({
      tsObject: this.tsObject,
      httpClient: this.dataService.httpClient,
    })(...args);
  }

  private buildCustomObjectPayload(entity: any): CustomObjectPayload {
    if (!entity.data) {
      return this.normalizeTemplateIdPayload({ data: { ...entity } });
    }

    return this.normalizeTemplateIdPayload({
      data: Object.keys(entity.data).reduce(
        (p, c) => {
          p[c] = entity[c];
          return p;
        },
        {
          ...entity.data,
          id: entity.id,
          template_id: entity.template_id,
          related_to_id: this.tsObject.id,
        },
      ),
    });
  }

  private normalizeTemplateIdPayload(payload: CustomObjectPayload): CustomObjectPayload {
    const nestedTemplateId = payload?.data?.template_id;

    if (nestedTemplateId === undefined) {
      return payload;
    }

    const data = { ...(payload.data ?? {}) };
    delete data.template_id;

    return {
      ...payload,
      template_id: payload.template_id ?? nestedTemplateId,
      data,
    };
  }
}
